import { API, DashboardFilter, DashboardWidget } from '@litlingo/client';
import { setURLParams, showErrorAlert } from 'actions';
import {
  addAllValuesToField,
  addAndRemoveValuesToTree,
  addFieldsToGroup,
  addValuesToGroup,
  addValueToTree,
  changeConditionalByFieldValue,
  changeConditionalType,
  clearSelectedFields,
  clearSelectedFilters,
  deleteDashboard,
  deleteDashboardFailure,
  deleteDashboardRequest,
  deleteDashboardSuccess,
  fetchAllDashboards,
  fetchAllDashboardsFailure,
  fetchAllDashboardsRequest,
  fetchAllDashboardsSuccess,
  removeFieldFromTree,
  removeFieldsFromGroup,
  removeValueFromTree,
  removeValuesFromGroup,
  removeValuesFromTree,
  setTree,
  upsertDashboard,
  upsertDashboardFailure,
  upsertDashboardRequest,
  upsertDashboardSuccess,
} from 'actions/dashboard';
import { apiClient as LitLingoClient } from 'client';
import { andFilters } from 'constants/filtersMappingValues';
import { cloneDeep } from 'lodash';
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { getTree } from 'selectors/dashboard';
import { SagaReturn } from 'types';
import {
  addAllValuesToField as addAllValuesToFieldAction,
  addFieldToGroup,
  addToGroup,
  changeConditional,
  changeConditionalByField,
  findFieldFromValue,
  removeField,
  removeFieldFromGroup,
  removeFields,
  removeFromGroup,
  removeValue,
  setNewValue,
  transformToString,
  Tree,
} from 'utils/parserTree';

function* deleteDashboardSaga(action: ReturnType<typeof deleteDashboard>): SagaReturn {
  const { payload } = action;
  const { id } = payload;
  yield put(deleteDashboardRequest(id));
  const response = (yield call(
    [LitLingoClient.resources.dashboards, 'delete'],
    id
  )) as API.Response<API.Campaigns.Delete>;
  if (response.error != null) {
    yield put(deleteDashboardFailure(response.error));
  } else {
    yield put(deleteDashboardSuccess(id));
  }
}

function* fetchAllDashboardsSaga(): SagaReturn {
  yield put(fetchAllDashboardsRequest());

  const response = (yield call([
    LitLingoClient.resources.dashboards,
    'list',
  ])) as API.Response<API.Dashboards.List>;
  if (response.error != null) {
    yield put(fetchAllDashboardsFailure(response.error));
  } else {
    yield put(fetchAllDashboardsSuccess(response.data));
  }
}

function* upsertDashboardSaga(action: ReturnType<typeof upsertDashboard>): SagaReturn {
  const {
    payload: {
      uuid,
      name,
      type = 'standard',
      config = {
        filters: [] as DashboardFilter[],
        widgets: [] as DashboardWidget[],
        primary_timezone: 'America/Chicago',
      },
      allowed_roles: allowedRoles,
    },
  } = action;

  const dashboard = { uuid, name, type, config, allowed_roles: allowedRoles };
  // eslint-disable-next-line no-console
  console.log(dashboard);
  yield put(upsertDashboardRequest(dashboard));

  const response = (yield call([LitLingoClient.resources.dashboards, 'upsert'], {
    data: dashboard,
  })) as API.Response<API.Dashboards.Upsert>;
  if (response.error != null) {
    yield put(upsertDashboardFailure(response.error));
  } else {
    yield put(upsertDashboardSuccess(response.data));
    yield put(fetchAllDashboards());
  }
}

function* addAndRemoveValuesToTreeSaga(
  action: ReturnType<typeof addAndRemoveValuesToTree>
): SagaReturn {
  const { valuesToAdd, fieldsToRemove } = action.payload;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    let newTree = tree;
    valuesToAdd.forEach((valueToAdd) => {
      newTree = setNewValue(
        newTree,
        valueToAdd.field,
        valueToAdd.value,
        valueToAdd.label,
        valueToAdd.isSingleValue
      );
    });

    newTree = removeFields(newTree, fieldsToRemove);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    yield put(showErrorAlert((e as Error).message));
  }
}

function* addNewValueToTreeSaga(action: ReturnType<typeof addValueToTree>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = setNewValue(
      tree,
      payload.field,
      payload.value,
      payload.label,
      payload.isSingleValue
    );

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* removeValueFromTreeSaga(action: ReturnType<typeof removeValueFromTree>): SagaReturn {
  const { payload } = action;
  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = removeValue(tree, payload.value, payload.field, payload.fieldId);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* removeValuesFromTreeSaga(action: ReturnType<typeof removeValuesFromTree>): SagaReturn {
  const { payload } = action;
  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);
    let newTree = tree;

    payload.values.forEach((v) => {
      newTree = removeValue(tree, v, payload.field);
    });

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* changeConditionalSaga(action: ReturnType<typeof changeConditionalType>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = changeConditional(tree, payload.node, payload.condition);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* changeConditionalByFieldSaga(
  action: ReturnType<typeof changeConditionalByFieldValue>
): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = changeConditionalByField(tree, payload.field, payload.condition);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* addValuesToGroupSaga(action: ReturnType<typeof addValuesToGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const field = findFieldFromValue(tree, payload.nodes[0].value);
    if (!field.length) return;
    if (!andFilters.includes(`${field}_and`)) return;
    const newTree = addToGroup(tree, payload.nodes);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
    yield put(clearSelectedFilters());
  } catch (e) {
    yield put(clearSelectedFilters());
    showErrorAlert((e as Error).message);
  }
}

function* removeValuesFromGroupSaga(action: ReturnType<typeof removeValuesFromGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = removeFromGroup(tree, payload.nodes);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
    yield put(clearSelectedFilters());
  } catch (e) {
    yield put(clearSelectedFilters());
    showErrorAlert((e as Error).message);
  }
}

function* addFieldsToGroupSaga(action: ReturnType<typeof addFieldsToGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);
    const newTree = addFieldToGroup(tree, payload.nodes);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
    yield put(clearSelectedFields());
  } catch (e) {
    yield put(clearSelectedFields());
    yield put(showErrorAlert((e as Error).message));
  }
}

function* removeFieldsFromGroupSaga(action: ReturnType<typeof removeFieldsFromGroup>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = removeFieldFromGroup(tree, payload.nodes);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
    yield put(clearSelectedFields());
  } catch (e) {
    yield put(clearSelectedFields());
    yield put(showErrorAlert((e as Error).message));
  }
}

function* removeFieldFromTreeSaga(action: ReturnType<typeof removeFieldFromTree>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = removeField(tree, payload.field, payload.id);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* addAllValuesToFieldSaga(action: ReturnType<typeof addAllValuesToField>): SagaReturn {
  const { payload } = action;

  try {
    const tree = cloneDeep((yield select(getTree)) as Tree);

    const newTree = addAllValuesToFieldAction(tree, payload.nodes, payload.field);

    yield put(
      setURLParams({
        metrics__filters_search: transformToString(newTree),
      })
    );
    yield put(setTree({ tree: newTree }));
  } catch (e) {
    showErrorAlert((e as Error).message);
  }
}

function* dashboardsSaga(): SagaReturn {
  yield takeLatest(fetchAllDashboards.toString(), fetchAllDashboardsSaga);
  yield takeLatest(deleteDashboard.toString(), deleteDashboardSaga);
  yield takeLatest(upsertDashboard.toString(), upsertDashboardSaga);

  // Dashboard filter tree
  yield takeLatest(addValueToTree.toString(), addNewValueToTreeSaga);
  yield takeLatest(removeValueFromTree.toString(), removeValueFromTreeSaga);
  yield takeLatest(removeValuesFromTree.toString(), removeValuesFromTreeSaga);
  yield takeLatest(addAndRemoveValuesToTree.toString(), addAndRemoveValuesToTreeSaga);
  yield takeLatest(changeConditionalType.toString(), changeConditionalSaga);
  yield takeLatest(addValuesToGroup.toString(), addValuesToGroupSaga);
  yield takeLatest(removeValuesFromGroup.toString(), removeValuesFromGroupSaga);
  yield takeLatest(addFieldsToGroup.toString(), addFieldsToGroupSaga);
  yield takeLatest(removeFieldsFromGroup.toString(), removeFieldsFromGroupSaga);
  yield takeLatest(removeFieldFromTree.toString(), removeFieldFromTreeSaga);
  yield takeLatest(addAllValuesToField.toString(), addAllValuesToFieldSaga);
  yield takeLatest(changeConditionalByFieldValue.toString(), changeConditionalByFieldSaga);
}

export default dashboardsSaga;
